import { Badge, Skeleton, tableHelpers, Tooltip } from '@round/creatorbase-ui';
import { asMoneyWithoutZeros, buildInstagramUserUrl, buildTiktokUserUrl } from 'helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { CellContext, ExplorePlatform } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { formatNumber, formatPercentage } from 'Modules/Plans/CreatorSearch/utils/creatorSearchUtils';
import { platformLabels, platformIcons, platformSolidIcons } from 'Modules/Plans/helpers';
import cn from 'classnames';
import { Fragment } from 'react';
import { influencer } from '@round/api';
import { getTableMeta } from '../../helpers';
import styles from './AccountsCell.module.css';

// A pool-local copy of Creator Explore's platform selector cell. It renders the same badge/selector, but
// only for platform accounts that belong to this pool, so a creator with one member account shows one
// badge and a creator with both shows both. The platform-selection mechanism (selected badge drives the
// metric columns, the other member acts as a toggle) is carried over unchanged.
const PLATFORMS: ExplorePlatform[] = ['tiktok', 'instagram'];

type Account = influencer.CreatorSearchResult['platforms']['tiktok'][number];

function PlatformIcon({ platform, isSolid }: { platform: ExplorePlatform; isSolid?: boolean }) {
    const ColorIcon = platformIcons[platform];
    const SolidIcon = platformSolidIcons[platform];
    const Icon = isSolid ? SolidIcon : ColorIcon;

    return <Icon className={styles.platformIcon} aria-hidden />;
}

export const AccountsCell = ({ row: { original }, table }: CellContext) => {
    const { isLoading, poolId, getRowSelectedPlatform, onRowPlatformSelect } = getTableMeta(table);
    const isBaseLoading = tableHelpers.isLoadingRow(original) || isLoading;
    const { getCurrencyById } = useCurrencies();

    if (isBaseLoading) {
        return (
            <div className={styles.container}>
                <Skeleton className={styles.pillSkeleton} />
                <Skeleton className={styles.iconSkeleton} />
            </div>
        );
    }

    // A platform account belongs to this pool when its `pool_ids` projection includes the current pool.
    const getPoolAccount = (platform: ExplorePlatform): Account | undefined =>
        original.platforms?.[platform]?.find(
            (account) => typeof poolId === 'number' && (account.pool_ids ?? []).includes(poolId)
        );

    const memberPlatforms = PLATFORMS.filter((platform) => !!getPoolAccount(platform));
    if (memberPlatforms.length === 0) {
        return null;
    }

    // Keep the selection anchored to a member so the highlighted badge is always one shown in this column;
    // the default selection can point at a platform the creator isn't a pool member on.
    const rowSelectedPlatform = getRowSelectedPlatform(original);
    const selectedPlatform = memberPlatforms.includes(rowSelectedPlatform) ? rowSelectedPlatform : memberPlatforms[0];

    return (
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
            {memberPlatforms.map((platform) => {
                const platformLabel = platformLabels[platform];
                const account = getPoolAccount(platform);

                if (platform === selectedPlatform) {
                    const profileUrl = account?.platform_username
                        ? platform === 'tiktok'
                            ? buildTiktokUserUrl(account.platform_username)
                            : buildInstagramUserUrl(account.platform_username)
                        : null;

                    return (
                        <Fragment key={platform}>
                            {profileUrl ? (
                                <Badge asChild className={cn(styles.pill, styles[platform])}>
                                    <a
                                        href={profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <PlatformIcon platform={platform} isSolid />
                                        {platformLabel}
                                    </a>
                                </Badge>
                            ) : (
                                <Badge className={cn(styles.pill, styles[platform])}>
                                    <PlatformIcon platform={platform} isSolid />
                                    {platformLabel}
                                </Badge>
                            )}
                        </Fragment>
                    );
                }

                const metrics = original.metrics?.[platform];

                return (
                    <Tooltip key={platform}>
                        <Tooltip.Trigger asChild>
                            <button
                                className={styles.platformIconButton}
                                aria-label={`Switch to ${platformLabel}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRowPlatformSelect(original._id, platform);
                                }}
                            >
                                <PlatformIcon platform={platform} />
                            </button>
                        </Tooltip.Trigger>

                        <Tooltip.Content side="bottom">
                            <div className={styles.tooltipStats}>
                                <p className={styles.tooltipPlatformLabel}>{platformLabel}</p>
                                <p className={styles.tooltipStatRow}>
                                    <span className={styles.tooltipStatLabel}>Followers</span>
                                    <span>{formatNumber(metrics?.total_follower_count)}</span>
                                </p>
                                <p className={styles.tooltipStatRow}>
                                    <span className={styles.tooltipStatLabel}>Cost</span>
                                    <span>
                                        {asMoneyWithoutZeros(account?.cost, getCurrencyById?.(account?.currency_id))}
                                    </span>
                                </p>
                                <p className={styles.tooltipStatRow}>
                                    <span className={styles.tooltipStatLabel}>Avg Plays</span>
                                    <span>{formatNumber(metrics?.avg_video_views_per_post)}</span>
                                </p>
                                <p className={styles.tooltipStatRow}>
                                    <span className={styles.tooltipStatLabel}>CPM</span>
                                    <span>
                                        {asMoneyWithoutZeros(
                                            account?.estimated_cpm,
                                            getCurrencyById?.(account?.currency_id)
                                        )}
                                    </span>
                                </p>
                                <p className={styles.tooltipStatRow}>
                                    <span className={styles.tooltipStatLabel}>ER%</span>
                                    <span>{formatPercentage(metrics?.avg_engagement_rate)}</span>
                                </p>
                            </div>
                        </Tooltip.Content>
                    </Tooltip>
                );
            })}
        </div>
    );
};
